import {
  getActivePromoBadge,
  type PromoBadgeStrapi,
} from '@/components/atoms/PromoBadge/utils';

import { APIPath, type PageResponse, type SinglePage } from '@/cms';
import type { Entry } from '@/cms/types/entry';

const unnecessaryFields = new Set([
  'createdAt',
  'updatedAt',
  'publishedAt',
  'createdBy',
  'updatedBy',
  'publishedBy',
  'localizations',
  'scraping_template_previews',
]);

const unnecessaryImageFields = new Set([
  'name',
  'caption',
  'hash',
  'ext',
  'mime',
  'size',
  'previewUrl',
  'provider',
  'provider_metadata',
  'formats',
]);

function isImageObject(obj: Record<string, unknown>): boolean {
  return typeof obj.url === 'string' && typeof obj.mime === 'string';
}

export function retainTopLevelFields<T>(
  reducedData: SinglePage<T>['data'],
  responseData: SinglePage<T>['data'],
  keys: (keyof T)[]
): SinglePage<T>['data'] {
  const payloadToAppend = keys.reduce((acc, key) => {
    acc[key as string] = responseData.attributes[key as keyof T];
    return acc;
  }, {} as Record<string, unknown>);

  const result: SinglePage<any>['data'] = {
    ...reducedData,
    attributes: {
      ...reducedData.attributes,
      ...payloadToAppend,
    },
  };
  return result;
}

export function recursivelyStripUnnecessaryFields<T>(data: T): T {
  if (data === null || data === undefined || typeof data !== 'object') {
    return data;
  }

  if (Array.isArray(data)) {
    return data.map(recursivelyStripUnnecessaryFields) as T;
  }

  const obj = data as Record<string, unknown>;
  const isImage = isImageObject(obj);
  const result: Record<string, unknown> = {};

  for (const key in obj) {
    if (unnecessaryFields.has(key)) continue;
    if (isImage && unnecessaryImageFields.has(key)) continue;
    if (key === 'promoBadge') {
      result[key] = getActivePromoBadge(obj[key] as PromoBadgeStrapi | null);
      continue;
    }
    result[key] = recursivelyStripUnnecessaryFields(obj[key]);
  }

  return result as T;
}

export function getShouldRetainTopLevelFields<T>(apiPath: APIPath): boolean {
  return apiPath === '/blogs';
}

export function grabDataFromResponse<T extends Entry = Entry>(
  response: PageResponse<T> | null | undefined
): SinglePage<T>['data'] | null {
  if (!response) return null;
  if (!response.data) return null;
  if (response.data.length === 0) return null;
  if (!response.data[0]) return null;

  return response.data[0];
}
